import { Box, Checkbox, FormControlLabel, Link, TextField, Tooltip } from "@mui/material"
import { HelpOutlined } from "@mui-symbols-material/w300"
import type { FC, ReactElement, ReactNode } from "react"
import type { ControllerRenderProps } from "react-hook-form"
import { Controller, useFormContext } from "react-hook-form"

import type { TechnicalInformationFormData } from "../schemas/TechnicalInformationSchema"

import { FormBooleanRadioGroup, FormNumericField } from "@/components/UiKit"

export const TechnicalInformationContent: FC = () => {
  const { control, watch } = useFormContext<TechnicalInformationFormData>()

  const handleNumericFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: ControllerRenderProps<TechnicalInformationFormData>
  ) => {
    const value = e.target.value
    field.onChange(value === "" ? "" : Number(value))
  }

  return (
    <form className='flex flex-col gap-y-6'>
      <Controller
        name='chemicalName'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label='Chemical Name'
            type='text'
            placeholder='Chemical Name'
            fullWidth
            variant='outlined'
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
      <Controller
        name='specificGravity'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label='Specific Gravity'
            type='number'
            value={field.value}
            slotProps={{
              input: {
                endAdornment: <HelpOutlined />,
                inputProps: {
                  className: "hide-stepper",
                  min: "0",
                  max: "99",
                  step: "0.00001",
                },
              },
            }}
            placeholder='0.00000'
            error={!!error}
            helperText={error?.message}
            onChange={(e) => handleNumericFieldChange(e, field)}
            data-testid='product.technical-info-form.specific-gravity-field'
          />
        )}
      />
      <FormNumericField
        control={control}
        name='shelfLifeMonths'
        label='Shelf Life (Months)'
        placeholder='0'
        decimalScale={0}
        allowNegative={false}
        data-testid='product.technical-info-form.shelf-life-field'
      />
      <TextFieldComponent
        control={control}
        name='harmonizedTariffSchedule'
        label='HTS'
        placeholder='000'
        tooltipText={
          <>
            HTS sets out the tariff rates and statistical categories for merchandise imported into the United States.
            <TooltipLink href='https://hts.usitc.gov/' text='More Info' />
          </>
        }
      />
      <Controller
        name='usmcaClass'
        control={control}
        render={({ field }) => (
          <Box className='flex items-center gap-x-2'>
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={Boolean(watch("usmcaClass"))}
                  role='checkbox'
                  onChange={(event) => field.onChange(event.target.checked)}
                  name='usmcaClass'
                  data-testid='product.technical-info-form.usmca-class-checkbox'
                />
              }
              label='USMCA (NAFTA)'
            />
            <Tooltip
              title={
                <>
                  Determine whether your goods qualify for USMCA
                  <TooltipLink
                    href='https://www.braumillerlaw.com/chemicals-and-the-usmca-its-not-just-how-you-mix-it-to-get-a-reaction-or-not-from-cbp/'
                    text='HERE'
                  />
                </>
              }
            >
              <HelpOutlined />
            </Tooltip>
          </Box>
        )}
      />
      <FormBooleanRadioGroup control={control} name='hazardStatusUs' label='Hazard Status: US' variant='yes-no' />
      <FormBooleanRadioGroup
        control={control}
        name='hazardStatusInternational'
        label='Hazard Status: International'
        variant='yes-no'
      />
    </form>
  )
}

export const TooltipLink = ({ text, href }: { text: string; href: string }): ReactElement => (
  <Link
    href={href}
    target='_blank'
    rel='noopener'
    className='ml-1 text-[#ACE1FF] decoration-[#ACE1FF]'
    underline='always'
  >
    {text}
  </Link>
)

interface TextFieldComponentProps {
  name: string
  type?: string
  label: string
  placeholder?: string
  control: any
  tooltipText?: ReactNode
  required?: boolean
  onChange?: (e: any, field: any) => void
}

/**
 * @deprecated Use UI Kit components instead.
 */
export const TextFieldComponent: FC<TextFieldComponentProps> = (props: TextFieldComponentProps) => {
  const { name, type = "text", control, placeholder, label, tooltipText, onChange } = props

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          label={label}
          type={type}
          value={field.value ?? ""}
          slotProps={{
            input: {
              endAdornment: tooltipText ? (
                <Tooltip title={tooltipText}>
                  <HelpOutlined />
                </Tooltip>
              ) : (
                <HelpOutlined />
              ),
              inputProps: {
                className: `${type === "number" ? "hide-stepper" : ""}`,
              },
            },
          }}
          placeholder={placeholder}
          fullWidth
          variant='outlined'
          error={!!error}
          helperText={error?.message}
          onChange={(e) => {
            field.onChange(onChange ? onChange(e, field) : e.target.value)
          }}
        />
      )}
    />
  )
}
